import { useAuth } from "@/hooks/useAuth";

/**
 * Frontend gate for the Banking & Finance route and navigation entries.
 * Mirrors the tightened RLS on public.bank_deposits and public.cash_movements
 * (see migration 20260722090000_restrict_banking_reads_to_managers.sql):
 * only global admin/manager or a direct store owner/admin/manager anywhere
 * in the tenant may access Banking. Cashiers, viewers, and users with no
 * store membership are excluded.
 *
 * `authorizationUserId === userId` guard mirrors the defensive check in
 * useStoreScopedManagerAccess -- role arrays are only trusted once useAuth
 * has resolved them for the CURRENT user, never a previous/stale user.
 *
 * This is defence in depth. The server-side RLS is the authoritative
 * boundary; this hook only prevents non-managers from mounting Banking
 * pages and firing queries that would return empty anyway.
 */
export function useBankingAccess(): { canView: boolean; loading: boolean } {
  const { user, roles, storeAccessRoles, authorizationUserId, loading } = useAuth();
  if (loading) return { canView: false, loading: true };
  const uid = user?.id ?? null;
  if (!uid || authorizationUserId !== uid) return { canView: false, loading: false };
  const globalOk = roles.includes("admin") || roles.includes("manager");
  const directOk = storeAccessRoles.some(
    (r) => r === "owner" || r === "admin" || r === "manager",
  );
  return { canView: globalOk || directOk, loading: false };
}